import { type CSSProperties } from 'typestyle/lib/types';
import StyleConstants from '../../../core/styles.constants';

/**
 * The hover card that says who has a desk.
 *
 * <p>
 * Sized in fixed pixels rather than in plan units: it lives in HTML above the SVG, so it keeps
 * its size and its legibility however far the plan is zoomed. `WIDTH` is also what the viewer
 * measures against the container edge to decide which side of the pointer to open on, so it is
 * exported rather than duplicated as a literal there.
 * </p>
 */
export const WIDTH = 268;

/** How far from the pointer the card sits, so the desk under it stays visible. */
export const POINTER_OFFSET = 14;

export const card: CSSProperties = {
  width: WIDTH,
  boxSizing: 'border-box',
  padding: 12,
  borderRadius: 10,
  backgroundColor: StyleConstants.WHITE_COLOR,
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.16)',
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  color: StyleConstants.GREY_COLOR,
  // Nothing in the card is interactive, and catching the pointer would end the hover that
  // opened it the moment the card grew under the cursor.
  pointerEvents: 'none',
};

export const header: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 8,
  fontSize: StyleConstants.TERTIARY_FONT_SIZE - 2,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: StyleConstants.LIGHT_GREY_COLOR,
};

export const roomName: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const dateHeading: CSSProperties = {
  fontSize: StyleConstants.TERTIARY_FONT_SIZE - 1,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: '8px 0 4px',
};

export const occupantRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '6px 0',
};

/** Every occupant after the first is separated, so a group booking reads as a list. */
export const occupantRowDivided: CSSProperties = {
  ...occupantRow,
  borderTop: '1px solid #EFEFEF',
};

export const avatar: CSSProperties = {
  flex: '0 0 auto',
  width: 36,
  height: 36,
  borderRadius: '50%',
  objectFit: 'cover',
  backgroundColor: '#F0F4FF',
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
};

/** Shown in place of a photo, which most employees have not uploaded. */
export const initials: CSSProperties = {
  ...avatar,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
  fontWeight: 700,
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const details: CSSProperties = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export const name: CSSProperties = {
  fontSize: StyleConstants.SECONDARY_FONT_SIZE,
  fontWeight: 600,
  color: StyleConstants.GREY_COLOR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const contactLine: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
  color: StyleConstants.GREY_COLOR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const contactIcon: CSSProperties = {
  flex: '0 0 auto',
  fontSize: 14,
  color: StyleConstants.LIGHT_GREY_COLOR,
};

/**
 * Marks a company medic, in the colour and with the icon the office's user listing uses for one,
 * so the portal does not teach two symbols for the same thing.
 */
export const medicLine: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
  color: StyleConstants.PINK_COLOR,
  fontWeight: 600,
};

/**
 * How the person came to hold the desk.
 *
 * Shown only when it is not their own booking: "reserved by them" is the ordinary case and
 * labelling it would be noise, while a group booking or a walk-up changes what the name means.
 */
export const sourceBadge: CSSProperties = {
  alignSelf: 'flex-start',
  marginTop: 2,
  padding: '1px 6px',
  borderRadius: 10,
  backgroundColor: '#F0F4FF',
  color: StyleConstants.DARK_BLUE_COLOR,
  fontSize: StyleConstants.TERTIARY_FONT_SIZE - 2,
  fontWeight: 600,
};

/** "+3 more", when a group is longer than the card shows. */
export const overflowCount: CSSProperties = {
  paddingTop: 6,
  fontSize: StyleConstants.TERTIARY_FONT_SIZE - 1,
  color: StyleConstants.GREY_COLOR,
  fontStyle: 'italic',
};

export const unknownOccupant: CSSProperties = {
  fontSize: StyleConstants.TERTIARY_FONT_SIZE,
  fontStyle: 'italic',
  color: StyleConstants.LIGHT_GREY_COLOR,
};
